import { Gender } from '@/constants/form_inputs';

import { IAddress, IAddressForm } from './address';
import { IAgency } from './agency';
import { IAuditModel, ObjectId } from './base';
import { IEquipment } from './equipment';
import { IFile } from './file';
import { ILocation, IMultiPolygon, IPoint } from './geo';

export enum Role {
  // Account roles
  ACCOUNT_ADMIN = 'ACCOUNT_ADMIN',
  DISPATCHER = 'DISPATCHER',
  VIEWER = 'VIEWER',
  CFS_VIEW_ONLY = 'CFS_VIEW_ONLY',
  ACCESS_TASKS = 'ACCESS_TASKS',

  // Master index roles
  MASTER_INDEX_ADDRESSES = 'MASTER_INDEX_ADDRESSES',
  MASTER_INDEX_PERSONS = 'MASTER_INDEX_PERSONS',
  MASTER_INDEX_VEHICLES = 'MASTER_INDEX_VEHICLES',

  // Agency roles
  AGENCY_ADMIN = 'AGENCY_ADMIN',
  FIRE_RMS = 'FIRE_RMS',
  FIRST_RESPONDER = 'FIRST_RESPONDER',
  EMPLOYEE = 'EMPLOYEE',
  RMS_CASES = 'RMS_CASES',
  KASE_APPROVER = 'KASE_APPROVER',
  EVIDENCE_COLLECTOR = 'EVIDENCE_COLLECTOR',
  EVIDENCE_TRANSPORTER = 'EVIDENCE_TRANSPORTER',
  EVIDENCE_CUSTODIAN = 'EVIDENCE_CUSTODIAN',
  JAIL_OFFICER = 'JAIL_OFFICER',
  NIBRS_REPORT_EXPORTER = 'NIBRS_REPORT_EXPORTER',
  SKYDIO_NOTIFICATIONS = 'SKYDIO_NOTIFICATIONS',

  // Calls roles
  CALL_LOG_ACCESS = 'CALL_LOG_ACCESS',
  CALL_RECORDING_ACCESS = 'CALL_RECORDING_ACCESS',

  // Tools roles
  TOOLS_MANAGER = 'TOOLS_MANAGER',

  // Internal Affairs roles
  IA_OFFICER = 'IA_OFFICER',

  // Complaint Policy roles (single role; UI shows "Complaint policy" + "Law text admin" for visibility)
  COMPLAINT_POLICY_LAW_TEXT_ADMIN = 'COMPLAINT_POLICY_LAW_TEXT_ADMIN',

  // RMS_CASES roles
  RMS_CASES_FULL_ACCESS = 'RMS_CASES_FULL_ACCESS',
  RMS_CASES_VIEW = 'RMS_CASES_VIEW',

  // Wanted Person roles
  WANTED_PERSON_EDIT = 'WANTED_PERSON_EDIT',
  WANTED_PERSON_VIEW = 'WANTED_PERSON_VIEW',
  WANTED_PERSON_VIEW_ADD_ATTEMPT = 'WANTED_PERSON_VIEW_ADD_ATTEMPT',

  // Calendar & Schedules roles
  CALENDAR_SCHEDULES_CREATE_EDIT_DELETE_OWN = 'CALENDAR_SCHEDULES_CREATE_EDIT_DELETE_OWN',
  CALENDAR_SCHEDULES_EDIT_DELETE_OTHERS = 'CALENDAR_SCHEDULES_EDIT_DELETE_OTHERS',
  CALENDAR_SCHEDULES_APPROVE_REQUESTS = 'CALENDAR_SCHEDULES_APPROVE_REQUESTS',

  // NCIC roles
  NCIC_AUTHORIZED = 'NCIC_AUTHORIZED',

  // Mobile roles
  ACCESS_INACTIVE_CFS = 'ACCESS_INACTIVE_CFS',

  // Office Chat roles
  ACCESS_OFFICE_CHAT = 'ACCESS_OFFICE_CHAT',
  OFFICE_CHAT_ADMIN = 'OFFICE_CHAT_ADMIN',

  // Sex Offender roles
  SEX_OFFENDER_MANAGE = 'SEX_OFFENDER_MANAGE',
}

export enum UserStatus {
  INACTIVE = 'INACTIVE',
  EMAIL_VERIFIED = 'EMAIL_VERIFIED',
  ACTIVE = 'ACTIVE',
  SUSPENDED = 'SUSPENDED',
}

export enum BloodType {
  A_POSITIVE = 'A+',
  A_NEGATIVE = 'A-',
  B_POSITIVE = 'B+',
  B_NEGATIVE = 'B-',
  O_POSITIVE = 'O+',
  O_NEGATIVE = 'O-',
  AB_POSITIVE = 'AB+',
  AB_NEGATIVE = 'AB-',
  UNKNOWN = 'UN',
}

export interface IRank extends IAuditModel {
  name: string;
  agency: string;
}

export interface IDepartment extends IAuditModel {
  name: string;
  agency: string;
}

export interface IContact {
  name: string;
  phoneNumber: string;
}

export interface IDriverLicence {
  number: string;
  expireDate: string;
}

export interface ICertification {
  name: string;
  expireDate: string;
  file?: IFile;
  required?: boolean | string;
}

export interface ISupervisor extends ObjectId {
  firstName?: string;
  lastName?: string;
  fullName?: string;
}

interface IEmploymentInformation {
  title: string;
  rank?: IRank;
  badgeNumber: string;
  department?: IDepartment;
  seniority: number;
  supervisor?: ISupervisor;
  stateId?: string;
}

interface IEmploymentInformationForm
  extends Omit<IEmploymentInformation, 'rank' | 'department' | 'supervisor'> {
  rank?: string;
  department?: string;
  supervisor?: string;
  stateId?: string;
}

export interface IUserRole {
  agency?: string | IAgency;
  role: Role;
}

export interface IProfileForm {
  gender: Gender;
  birthday: string;
  bloodType: BloodType;
  address?: IAddressForm;
  emergencyContact: IContact;
  certifications: ICertification[];
  employmentInformation: IEmploymentInformationForm;
}

export interface IProfile
  extends Omit<IProfileForm, 'address' | 'employmentInformation'> {
  address: IAddress;
  employmentInformation: IEmploymentInformation;
}

export interface IProfileReq extends Omit<IProfileForm, 'address'> {
  address?: string;
}

export interface IUserForm extends ObjectId {
  email: string;
  username?: string;
  phoneNumber?: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  stateId?: string;
  roles: IUserRole[];
  profile: IProfileForm;
}

export interface IUserReq extends Omit<IUserForm, 'profile'> {
  profile: IProfileReq;
}

export interface IUser extends IAuditModel, Omit<IUserForm, 'profile'> {
  profile: IProfile;
  profileInfo?: IProfile;
  isLoginEnabled: boolean;
  isNewDevicesRestricted?: boolean;
  status?: UserStatus;
  latsLoggedIn?: Date;
  location?: ILocation;
  stateId?: string;
  gpsTracker?: IEquipment;
  readonly fullName: string;
  mobileAppSettings?: IMobileAppSettings;
  settings?: Record<string, any>;
}

export enum AccountStatus {
  INACTIVE = 'INACTIVE',
  ACTIVE = 'ACTIVE',
}

export interface IFeaturesSetting {
  showNearestIntersectionWeb?: boolean;
  isEnabledInventoryLocation: boolean;
  isEnabledVendors: boolean;
  isEnabledUnderFire: boolean;
  isEnabledAutoTaskCreation: boolean;
  cfsExternalService?: string;
  isEnabledAIGeneratedCFSDescription: boolean;
  isEnabledRapidSOSForDifferingReporter?: boolean;
  isEnabledCarStopCreation?: boolean;
  isEnabledCFSCreation?: boolean;
  isEnabledIncidentReportCreation?: boolean;
  isEnabledJSMModule?: boolean;
  isEnabledRadioClicksModule?: boolean;
  mapMarkerTypes?: Record<string, boolean>;
  isEnabledCustomUnitStatus?: boolean;
  isEnabledCLI?: boolean;
  isEnabledAutomaticRMSCreation?: boolean;
  isEnabledNursingHome?: boolean;
  neris?: {
    enabled: boolean;
    entityId: string;
  };
}

export interface IAccountSettings {
  allowCFSAI: boolean;
  autoCFSRTD: boolean;
  groupCFSRequiredUnits: boolean;
  files?: IFile[];
  webhookSecret?: string; // VOIP dialpad webhook secret key. This jwt secret is used to create webhook in dialpad.
  center?: IPoint;
  areas?: IMultiPolygon | null;
  defaultZoom?: number;
  hqAddress?: IAddress | null;
  headquarters?: IHeadquarters[];
  numberOfNearestResponders: number;
  features?: IFeaturesSetting | null;
  demoMode?: boolean;
  timezone?: string; // Account timezone - examples America/New_York, Europe/Berlin
  auth?: {
    logoutOnClose?: boolean;
  };
}

export interface IHeadquarters {
  name: string;
  address: IAddress | string;
}

export interface IAccount {
  _id?: string;
  name: string;
  phoneNumber: string;
  email?: string;
  number?: string;
  address: IAddress;
  contact1: IContact;
  contact2: IContact;
  subdomain?: string;
  ips: string[];
  status?: AccountStatus;
  settings: IAccountSettings;
}

export interface IMobileAppSettings {
  canViewClosedCFS: boolean;
  canViewLiveInformation: boolean;
  canEditLiveInformation: boolean;
}
